/**
 * Centralised analytics + consent integration.
 *
 * - Implements Google Consent Mode v2 (default: all denied → granted on user opt-in)
 * - Lazy-loads gtag.js (GA4 + Google Ads) and fbq.js (Meta Pixel) only when their IDs are present
 * - Exposes trackEvent / trackPageView / trackConversion helpers that no-op if not configured
 *
 * ENV vars (frontend/.env):
 *   REACT_APP_GA4_ID                                 — "G-XXXXXXX"  (Google Analytics 4)
 *   REACT_APP_META_PIXEL_ID                          — "1234567890123456"  (Meta Pixel)
 *   REACT_APP_GADS_CONVERSION_ID                     — "AW-1234567890"     (Google Ads)
 *   REACT_APP_GADS_CONVERSION_LABEL_QUOTE            — "abcDEF123"
 *   REACT_APP_GADS_CONVERSION_LABEL_CONTACT          — "abcDEF456"
 *   REACT_APP_GADS_CONVERSION_LABEL_LEAD_MAGNET      — "abcDEF789"
 */

const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const GADS_ID = process.env.REACT_APP_GADS_CONVERSION_ID || "";
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID || "";
const GADS_LABELS = {
  quote: process.env.REACT_APP_GADS_CONVERSION_LABEL_QUOTE || "",
  contact: process.env.REACT_APP_GADS_CONVERSION_LABEL_CONTACT || "",
  lead_magnet: process.env.REACT_APP_GADS_CONVERSION_LABEL_LEAD_MAGNET || "",
};

let initialised = false;

// Boot dataLayer + gtag stub AT MODULE LOAD (before React mounts), so any early
// trackPageView / trackEvent calls (esp. from PageViewTracker child effect that
// runs before ConsentProvider parent effect) are safely queued and flushed once
// the real gtag.js script loads.
if (typeof window !== "undefined" && (GA4_ID || GADS_ID)) {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  // Consent Mode v2 defaults must be set BEFORE any config/event is queued.
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
  window.gtag("js", new Date());
  if (GA4_ID) window.gtag("config", GA4_ID, { send_page_view: false });
  if (GADS_ID && GADS_ID !== GA4_ID) window.gtag("config", GADS_ID);
}

function loadGtag() {
  if (!GA4_ID && !GADS_ID) return;
  const id = GA4_ID || GADS_ID;
  if (document.getElementById("gtag-base")) return;
  const s = document.createElement("script");
  s.id = "gtag-base";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);
}

function loadPixelIfConsented(consent) {
  // Meta Pixel has no native consent mode: the fbevents.js script itself is
  // only injected AFTER the visitor grants marketing consent.
  if (!PIXEL_ID) return;
  if (!consent?.marketing) return;
  if (window.__fbqInitialised) return;
  if (!window.fbq) {
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){
      if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];
      t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s);
    }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
  }
  window.fbq("init", PIXEL_ID);
  window.fbq("track", "PageView");
  window.__fbqInitialised = true;
}

function loadClarityIfConsented(consent) {
  if (!CLARITY_ID) return;
  if (!consent?.analytics) return;
  if (window.__clarityLoaded) return;
  window.__clarityLoaded = true;
  /* eslint-disable */
  (function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
  })(window, document, "clarity", "script", CLARITY_ID);
  /* eslint-enable */
}

export function initAnalytics() {
  if (initialised) return;
  initialised = true;
  // Laad gtag.js pas bij eerste interactie (of na 5s) — haalt ~185ms blocking
  // time van het kritieke pad. Events vóór het laden worden gequeued in de
  // dataLayer-stub en daarna geflusht.
  let fired = false;
  const evs = ["pointerdown", "keydown", "touchstart", "scroll"];
  const fire = () => {
    if (fired) return;
    fired = true;
    evs.forEach((e) => window.removeEventListener(e, fire));
    loadGtag();
  };
  evs.forEach((e) => window.addEventListener(e, fire, { passive: true }));
  setTimeout(fire, 5000);
}

export function updateConsent(consent) {
  // consent = { analytics: boolean, marketing: boolean }
  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: consent?.analytics ? "granted" : "denied",
      ad_storage: consent?.marketing ? "granted" : "denied",
      ad_user_data: consent?.marketing ? "granted" : "denied",
      ad_personalization: consent?.marketing ? "granted" : "denied",
    });
  }
  loadPixelIfConsented(consent);
  loadClarityIfConsented(consent);
}

export function trackPageView(path) {
  if (window.gtag && GA4_ID) {
    window.gtag("event", "page_view", { page_path: path, page_location: window.location.href });
  }
  if (window.fbq && window.__fbqInitialised) {
    window.fbq("track", "PageView");
  }
}

export function trackEvent(name, params = {}) {
  if (window.gtag) window.gtag("event", name, params);
  if (window.fbq && window.__fbqInitialised) {
    // Standard Meta events for high-value leads (used for Ads optimisation)
    const metaStandard = {
      quote_submitted: "Lead",
      contact_submitted: "Lead",       // user spec: contact_submit → Lead
      lead_magnet_submitted: "Lead",   // fired by trackConversion('lead_magnet',...)
    };
    // Custom Meta events (visible in Events Manager but not standard optimisation targets)
    const metaCustom = new Set(["quote_started", "phone_click", "whatsapp_click"]);
    if (metaStandard[name]) {
      window.fbq("track", metaStandard[name], {
        content_name: params.service || name,
        content_category: name,
        value: params.value || 0,
        currency: "EUR",
      });
    } else if (metaCustom.has(name)) {
      window.fbq("trackCustom", name, params);
    }
  }
}

// De-dupe registry for once-per-session events (avoids duplicate spam on rapid re-renders)
const _firedOnce = new Set();
export function trackOnce(name, params = {}) {
  if (_firedOnce.has(name)) return;
  _firedOnce.add(name);
  trackEvent(name, params);
}

/**
 * Track outbound intent clicks (phone / WhatsApp). Attach as onClick to <a href="tel:..."> or WhatsApp links.
 * `channel` = 'phone' | 'whatsapp'  ·  `location` = where on the page (footer, navbar, sticky, hero, ...)
 */
export function trackClick(channel, location = "unknown", extra = {}) {
  const eventName = channel === "phone" ? "phone_click" : channel === "whatsapp" ? "whatsapp_click" : `${channel}_click`;
  trackEvent(eventName, { link_location: location, ...extra });
}

/**
 * Fire a Google Ads conversion + GA4 event + Meta lead event in one call.
 * type = 'quote' | 'contact' | 'lead_magnet'
 */
export function trackConversion(type, value = 0, extra = {}) {
  trackEvent(`${type}_submitted`, { value, currency: "EUR", ...extra });
  if (window.gtag && GADS_ID && GADS_LABELS[type]) {
    window.gtag("event", "conversion", {
      send_to: `${GADS_ID}/${GADS_LABELS[type]}`,
      value,
      currency: "EUR",
      transaction_id: extra.transaction_id || "",
    });
  }
}

export const analyticsConfig = {
  ga4: !!GA4_ID,
  meta: !!PIXEL_ID,
  gads: !!GADS_ID,
  clarity: !!CLARITY_ID,
};
